import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { vendorsAPI, departmentsAPI } from '../services/api';
import { useApi } from '../hooks/useApi';
import { Vendor, Department } from '../types';
import VendorProgressChart from '../components/VendorProgressChart';
import Pagination from '../components/Pagination';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import {
  Plus,
  Search,
  Building2,
  Edit,
  AlertCircle,
  X,
  Mail,
  Phone,
  MapPin,
  User,
  BarChart3,
  ChevronDown,
  Trash2,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { errorModal } from '../components/ErrorModal';
import Spinner from '../components/Spinner';
import { decryptData } from '../utils/encryption';
import { confirmToast } from '../components/confirmToast';

const Vendors: React.FC = () => {
  const navigate = useNavigate();
  const userPermissions = decryptData(localStorage.getItem('permissions') || '') as string;

  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showChartModal, setShowChartModal] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    total_pages: 0
  });

  const { data: vendors, loading, error, refetch } = useApi<Vendor[]>(
    async () => {
      const response = await vendorsAPI.getAll({ 
        page, 
        limit, 
        search: debouncedSearch || undefined, 
        order: 'asc' 
      });
      
      // Extract pagination info from response
      if (response.success && response.pagination) {
        setPagination(response.pagination);
      }
      
      return response;
    },
    [debouncedSearch, page, limit]
  );

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 350);
    return () => clearTimeout(id);
  }, [searchTerm]);

  useEffect(() => {
    // Reset to first page whenever search changes
    setPage(1);
  }, [debouncedSearch]);

  type FormDataType = {
    name: string;
    email: string;
    phone: string;
    address: string;
    city: string;
    state: string;
    zip_code: string;
    country: string;
    contact_person: string;
    notes: string;
    registration_number: string;
    department_id: string;
    is_active: boolean;
  };

  const [formData, setFormData] = useState<FormDataType>({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    country: '',
    contact_person: '',
    notes: '',
    registration_number: '',
    department_id: '',
    is_active: true
  });

  const [errors, setErrors] = useState<Record<keyof FormDataType, string>>({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    country: '',
    contact_person: '',
    notes: '',
    registration_number: '',
    department_id: '',
    is_active: ''
  });

  const [submitted, setSubmitted] = useState(false);
  const [departmentDropdown, setDepartmentDropdown] = useState<Department[]>([]);
  
  // Searchable dropdown states
  const [departmentDropdownOpen, setDepartmentDropdownOpen] = useState(false);
  const [departmentSearchTerm, setDepartmentSearchTerm] = useState('');
  const departmentDropdownRef = useRef<HTMLDivElement>(null);

  const fetchDepartmentDropdown = async () => {
    try {
      const response = await departmentsAPI.dropDown();
      if (response.success) {
        setDepartmentDropdown(response.data);
      }
    } catch (err: any) {
      console.error('Failed to fetch department dropdown:', err);
    }
  };

  useEffect(() => {
    fetchDepartmentDropdown();
  }, []);

  // Handle clicks outside dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (departmentDropdownRef.current && !departmentDropdownRef.current.contains(event.target as Node)) {
        setDepartmentDropdownOpen(false);
        setDepartmentSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const validateField = (field: keyof FormDataType, value: string): string => {
    if (field === 'name') {
      if (!value.trim()) return 'Vendor name / Expenses head is required.';
      return '';
    }
    if (field === 'phone') {
      const trimmed = value.trim();
      if (!trimmed) return '';
      const digitsOnly = trimmed.replace(/\D/g, '');
      if (digitsOnly.length < 10) return 'Enter a valid phone number.';
      return '';
    }
    if (field === 'department_id') {
      if (!value.trim()) return 'Department is required.';
      return '';
    }
    if (field === 'registration_number') {
      // Optional field - only validate length if provided
      if (value.trim() && value.trim().length < 3) return 'Registration number must be at least 3 characters.';
      return '';
    }
    return '';
  };

  const validateForm = (data: FormDataType) => {
    const nextErrors = { ...errors } as Record<keyof FormDataType, string>;
    nextErrors.name = validateField('name', data.name);
    nextErrors.email = validateField('email', data.email);
    nextErrors.phone = validateField('phone', data.phone);
    nextErrors.department_id = validateField('department_id', data.department_id);
    nextErrors.registration_number = validateField('registration_number', data.registration_number);
    nextErrors.zip_code = validateField('zip_code', data.zip_code);
    return nextErrors;
  };

  const clearErrors = () => {
    setErrors({
      name: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      zip_code: '',
      country: '',
      contact_person: '',
      notes: '',
      registration_number: '',
      department_id: '',
      is_active: ''
    });
    setSubmitted(false);
  };

  const handleFieldChange = (field: keyof FormDataType, value: string | boolean) => {
    setFormData({ ...formData, [field]: value });
    if (submitted || errors[field]) {
      const message = validateField(field, typeof value === 'string' ? value : '');
      setErrors({ ...errors, [field]: message });
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      zip_code: '',
      country: '',
      contact_person: '',
      notes: '',
      registration_number: '',
      department_id: '',
      is_active: true
    });
    clearErrors();
    // Reset dropdown states
    setDepartmentDropdownOpen(false);
    setDepartmentSearchTerm('');
  };

  const serverVendors = vendors || [];
  const isInitialLoading = loading && !vendors;

  // Create pagination object for the Pagination component
  const paginationInfo = {
    page: pagination.page,
    limit: pagination.limit,
    total: pagination.total,
    total_pages: pagination.total_pages
  };

  // Handlers for pagination
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
  };

  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    const nextErrors = validateForm(formData);
    setErrors(nextErrors);
    const hasErrors = Object.values(nextErrors).some((val) => val);
    if (hasErrors) return;

    setIsSubmitting(true);
    try {
      // Prepare the data, always including optional fields (even when empty)
      const vendorData: any = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone.trim(),
        address: formData.address.trim(),
        city: formData.city.trim(),
        state: formData.state.trim(),
        zip_code: formData.zip_code.trim(),
        country: formData.country.trim(),
        contact_person: formData.contact_person.trim(),
        notes: formData.notes.trim(),
        registration_number: formData.registration_number.trim(),
        department_id: formData.department_id,
        is_active: formData.is_active
      };

      const response = await vendorsAPI.create(vendorData);
      if (response.success) {
        toast.success('Vendor created successfully');
      } else {
        errorModal(response.error || 'Error creating vendor. Please try again.');
      }
      setShowCreateModal(false);
      resetForm();
      refetch();
    } catch (error) {
      console.error('Error creating vendor:', error);
      errorModal('Error creating vendor. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedVendor) return;
    setSubmitted(true);
    const nextErrors = validateForm(formData);
    setErrors(nextErrors);
    const hasErrors = Object.values(nextErrors).some((val) => val);
    if (hasErrors) return;

    setIsSubmitting(true);
    try {
      // Prepare the data, always including optional fields (even when empty)
      const vendorData: any = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone.trim(),
        address: formData.address.trim(),
        city: formData.city.trim(),
        state: formData.state.trim(),
        zip_code: formData.zip_code.trim(),
        country: formData.country.trim(),
        contact_person: formData.contact_person.trim(),
        notes: formData.notes.trim(),
        registration_number: formData.registration_number.trim(),
        department_id: formData.department_id,
        is_active: formData.is_active
      };

      const response = await vendorsAPI.update(selectedVendor.id, vendorData);
      if (response.success) {
        toast.success('Vendor updated successfully');
      } else {
        errorModal(response.error || 'Error updating vendor. Please try again.');
      }
      setShowEditModal(false);
      setSelectedVendor(null);
      resetForm();
      refetch();
    } catch (error) {
      console.error('Error updating vendor:', error);
      errorModal('Error updating vendor. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (vendor: Vendor) => {
    setSelectedVendor(vendor);
    setFormData({
      name: vendor.name,
      email: vendor.email || '',
      phone: vendor.phone || '',
      address: vendor.address || '',
      city: vendor.city || '',
      state: vendor.state || '',
      zip_code: vendor.zip_code || '',
      country: vendor.country || '',
      contact_person: vendor.contact_person || '',
      notes: vendor.notes || '',
      registration_number: vendor.registration_number || '',
      department_id: vendor.department_id?.toString() || '',
      is_active: vendor.is_active
    });
    clearErrors();
    setShowEditModal(true);
  };

  const handleDelete = async (vendor: Vendor) => {
    confirmToast('Are you sure you want to delete this vendor? This action cannot be undone.', async () => {
      try {
        const response = await vendorsAPI.delete(vendor.id);
        if (response.success) {
          toast.success('Vendor deleted successfully');
          refetch();
        } else {
          errorModal(response.error || 'Error deleting vendor. Please try again.');
        }
      } catch (err: any) {
        errorModal(err.response?.data?.error || 'Error deleting vendor. Please try again.');
      }
    });
  };


  if (isInitialLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg bg-red-50 p-4 border border-red-200">
        <div className="flex">
          <div className="flex-shrink-0">
            <AlertCircle className="h-5 w-5 text-red-400" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">
              Error loading vendors
            </h3>
            <div className="mt-2 text-sm text-red-700">
              {error}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full px-4 py-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Vendors</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage your vendor relationships and contact information.
          </p>
        </div>

{ userPermissions === "admin" && (
        <div className="mt-4 sm:mt-0 flex space-x-3">
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary flex items-center"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Vendor
          </button>
          <button
            onClick={() => navigate('/vendors/import')}
            className="btn-secondary flex items-center">
            Import vendor
          </button>
          </div>
        )}
      </div>

      {/* Search and Filters */}
      <div className="card">
        <div className="card-body">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  className="input-field pl-10"
                  placeholder="Search vendors..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {loading && vendors && (
                  <div className="absolute inset-y-0 right-3 flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-300 border-t-primary-600"></div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Vendors Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6">
        {serverVendors.map((vendor) => (
          <div key={vendor.id} className="card hover:shadow-lg transition-shadow">
            <div className="card-body">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center space-x-3 min-w-0 flex-1">
                  <div className="flex-shrink-0">
                    <div className="h-10 w-10 bg-primary-100 rounded-lg flex items-center justify-center">
                      <Building2 className="h-5 w-5 text-primary-600" />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-medium text-gray-900 break-words line-clamp-2">
                      {vendor.name}
                    </h3>
                    {vendor.contact_person && (
                      <p className="text-sm text-gray-500 flex items-center mt-1 min-w-0">
                        <User className="h-4 w-4 mr-1 flex-shrink-0" />
                        <span className="truncate">{vendor.contact_person}</span>
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-2 flex-shrink-0">
                  <button
                    onClick={() => {
                      setSelectedVendor(vendor);
                      setShowChartModal(true);
                    }}
                    className="p-2 text-gray-600 hover:text-green-600 transition-colors"
                    title="View Progress Chart"
                  >
                    <BarChart3 className="h-4 w-4" />
                  </button>
                  { userPermissions === "admin" && (
                  <button
                    onClick={() => handleEdit(vendor)}
                    className="p-2 text-gray-600 hover:text-primary-600 transition-colors"
                    title="Edit vendor"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  )}
                  { userPermissions === "admin" && (
                  <button
                    onClick={() => handleDelete(vendor)}
                    className="p-2 text-gray-600 hover:text-red-600"
                    title="Delete vendor"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                  )}
                  {/* <button
                    onClick={() => navigate(`/vendors/${vendor.id}/onboarding`)}
                    className="p-2 text-gray-600 hover:text-green-600"
                    title="Vendor Onboarding"
                  >
                    <FileText className="h-4 w-4" />
                  </button> */}
                </div>
              </div>

              <div className="mt-4 space-y-2">
                {vendor.email && (
                  <div className="flex items-start text-sm text-gray-600 min-w-0">
                    <Mail className="h-4 w-4 mr-2 text-gray-400 flex-shrink-0 mt-0.5" />
                    <span className="break-words min-w-0">{vendor.email}</span>
                  </div>
                )}
                {vendor.phone && (
                  <div className="flex items-center text-sm text-gray-600">
                    <Phone className="h-4 w-4 mr-2 text-gray-400 flex-shrink-0" />
                    <span className="break-words">{vendor.phone}</span>
                  </div>
                )}
                {vendor.registration_number && (
                  <div className="flex items-center text-sm text-gray-600">
                    <Building2 className="h-4 w-4 mr-2 text-gray-400 flex-shrink-0" />
                    <span className="break-words">Reg: {vendor.registration_number}</span>
                  </div>
                )}
                {(vendor.city || vendor.state) && (
                  <div className="flex items-center text-sm text-gray-600">
                    <MapPin className="h-4 w-4 mr-2 text-gray-400 flex-shrink-0" />
                    <span className="break-words">
                      {[vendor.city, vendor.state].filter(Boolean).join(', ')}
                    </span>
                  </div>
                )}
              </div>

              <div className="mt-4 flex items-center justify-between gap-2">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${vendor.is_active
                  ? 'bg-green-100 text-green-800'
                  : 'bg-red-100 text-red-800'
                  }`}>
                  {vendor.is_active ? 'Active' : 'Inactive'}
                </span>
                <span className="text-xs text-gray-500 whitespace-nowrap">
                  Added {new Date(vendor.created_at).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {serverVendors.length === 0 && (
        <div className="text-center py-12">
          <Building2 className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No vendors found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm ? 'Try adjusting your search terms.' : 'Get started by adding your first vendor.'}
          </p>
          {!searchTerm && (
            <div className="mt-6">
              <button
                onClick={() => setShowCreateModal(true)}
                className="btn-primary flex items-center mx-auto"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Vendor
              </button>
            </div>
          )}
        </div>
      )}

      {/* Pagination Controls */}
      <Pagination
        pagination={paginationInfo}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
        loading={loading}
      />

      {/* Create Vendor Modal */}
      {showCreateModal && (
        <div style={{ margin: '0px !important' }}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Add Vendor</h3>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleCreateSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Registration Number</label>
                <input
                  type="text"
                  className={`input-field ${errors.registration_number ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`}
                  value={formData.registration_number}
                  onChange={(e) => handleFieldChange('registration_number', e.target.value)}
                  placeholder="Enter registration number"
                />
                {errors.registration_number && (
                    <div className="mt-1 text-sm text-red-600 flex items-center">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      {errors.registration_number}
                    </div>
                  )}
              </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Vendor Name / Expenses Head *</label>
                  <input
                    type="text"
                    className={`input-field ${errors.name ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`}
                    value={formData.name}
                    onChange={(e) => handleFieldChange('name', e.target.value)}
                    placeholder="Enter vendor name / expenses head"
                  />
                  {errors.name && (
                    <div className="mt-1 text-sm text-red-600 flex items-center">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      {errors.name}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    type="email"
                    className="input-field"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="Enter email address"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Contact Person</label>
                  <input
                    type="text"
                    className="input-field"
                    value={formData.contact_person}
                    onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                    placeholder="Enter contact person name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <PhoneInput
                    country={'gb'}
                    value={formData.phone}
                    onChange={(value) => handleFieldChange('phone', value)}
                    inputStyle={{
                      width: '100%',
                      height: '42px',
                      border: errors.phone ? '1px solid #ef4444' : '1px solid #d1d5db',
                      borderRadius: '6px',
                      paddingLeft: '48px'
                    }}
                    buttonStyle={{
                      backgroundColor: '#f9fafb',
                      border: errors.phone ? '1px solid #ef4444' : '1px solid #d1d5db',
                      borderRadius: '6px 0 0 6px'
                    }}
                    dropdownStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px'
                    }}
                    preferredCountries={['us', 'gb', 'ca', 'au', 'in']}
                    placeholder="Enter phone number"
                    disabled={isSubmitting}
                  />
                  {errors.phone && (
                    <div className="mt-1 text-sm text-red-600 flex items-center">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      {errors.phone}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Owner Department *</label>
                  <div ref={departmentDropdownRef} className="relative">
                    <button
                      type="button"
                      onClick={() => setDepartmentDropdownOpen(!departmentDropdownOpen)}
                      className={`input-field w-full flex items-center justify-between text-left ${errors.department_id ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`}
                      disabled={isSubmitting}
                    >
                      <span className={formData.department_id ? 'text-gray-900' : 'text-gray-500'}>
                        {formData.department_id
                          ? departmentDropdown.find(d => d.id.toString() === formData.department_id)?.department || 'Select Department'
                          : 'Select Department'}
                      </span>
                      <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${departmentDropdownOpen ? 'transform rotate-180' : ''}`} />
                    </button>
                    {departmentDropdownOpen && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-hidden">
                        <div className="p-2 border-b border-gray-200">
                          <div className="relative">
                            <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                            <input
                              type="text"
                              className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                              placeholder="Search departments..."
                              value={departmentSearchTerm}
                              onChange={(e) => setDepartmentSearchTerm(e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                        </div>
                        <div className="max-h-48 overflow-y-auto">
                          {departmentDropdown
                            .filter(dept => 
                              dept.department.toLowerCase().includes(departmentSearchTerm.toLowerCase())
                            )
                            .map((dept) => (
                              <button
                                key={dept.id}
                                type="button"
                                className="w-full text-left px-4 py-2 text-sm text-gray-900 hover:bg-primary-50 hover:text-primary-900 focus:bg-primary-50 focus:text-primary-900 focus:outline-none"
                                onClick={() => {
                                  handleFieldChange('department_id', dept.id.toString());
                                  setDepartmentDropdownOpen(false);
                                  setDepartmentSearchTerm('');
                                }}
                              >
                                {dept.department}
                              </button>
                            ))}
                          {departmentDropdown.filter(dept => 
                            dept.department.toLowerCase().includes(departmentSearchTerm.toLowerCase())
                          ).length === 0 && (
                            <div className="px-4 py-2 text-sm text-gray-500 text-center">
                              No departments found
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  {errors.department_id && (
                    <div className="mt-1 text-sm text-red-600 flex items-center">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      {errors.department_id}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Address</label>
                <input
                  type="text"
                  className="input-field"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  disabled={isSubmitting}
                  placeholder="Enter address"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                <div>
                  <label className="block text-sm font-medium text-gray-700">Country</label>
                  <input
                    type="text"
                    className="input-field"
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    disabled={isSubmitting}
                    placeholder="Enter country"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">State</label>
                  <input
                    type="text"
                    className="input-field"
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    disabled={isSubmitting}
                    placeholder="Enter state"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">City</label>
                  <input
                    type="text"
                    className="input-field"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    disabled={isSubmitting}
                    placeholder="Enter city"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Post Code</label>
                  <input
                    type="text"
                    className={`input-field ${errors.zip_code ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`}
                    value={formData.zip_code}
                    onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                    disabled={isSubmitting}
                    placeholder="Enter post code"
                  />
                  {errors.zip_code && (
                    <div className="mt-1 text-sm text-red-600 flex items-center">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      {errors.zip_code}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Notes</label>
                <textarea
                  className="input-field"
                  rows={3}
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  disabled={isSubmitting}
                  placeholder="Enter notes"
                />
              </div>
{/* 
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_active_create"
                  checked={formData.is_active}
                  onChange={(e) => handleFieldChange('is_active', e.target.checked)}
                  disabled={isSubmitting}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="is_active_create" className="ml-2 block text-sm font-medium text-gray-700">
                  Active Vendor
                </label>
              </div> */}

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary"
                >
                  {isSubmitting ? <Spinner /> : 'Add Vendor'}
                </button>
              </div>
            </form>
          </div>
        </div>
        </div>
      )}

      {/* Edit Vendor Modal */}
      {showEditModal && selectedVendor && (
        <div style={{ margin: '0px !important' }}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Edit Vendor</h3>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedVendor(null);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Registration Number</label>
                <input
                  type="text"
                  className={`input-field ${errors.registration_number ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`}
                  value={formData.registration_number}
                  onChange={(e) => handleFieldChange('registration_number', e.target.value)}
                  placeholder="Enter registration number"
                  disabled={isSubmitting}
                />
                {errors.registration_number && (
                    <div className="mt-1 text-sm text-red-600 flex items-center">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      {errors.registration_number}
                    </div>
                  )}
              </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Vendor Name / Expenses Head *</label>
                  <input
                    type="text"
                    className={`input-field ${errors.name ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`}
                    value={formData.name}
                    onChange={(e) => handleFieldChange('name', e.target.value)}
                    disabled={isSubmitting}
                    placeholder="Enter vendor name / expenses head"
                  />
                  {errors.name && (
                    <div className="mt-1 text-sm text-red-600 flex items-center">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      {errors.name}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    type="email"
                    className="input-field"
                    value={formData.email}
                    onChange={(e) => handleFieldChange('email', e.target.value)}
                    disabled={isSubmitting}
                    placeholder="Enter email address"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Contact Person</label>
                  <input
                    type="text"
                    className="input-field"
                    value={formData.contact_person}
                    onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                    disabled={isSubmitting}
                    placeholder="Enter contact person name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <PhoneInput
                    country={'us'}
                    value={formData.phone}
                    onChange={(value) => handleFieldChange('phone', value)}
                    inputStyle={{
                      width: '100%',
                      height: '42px',
                      border: errors.phone ? '1px solid #ef4444' : '1px solid #d1d5db',
                      borderRadius: '6px',
                      paddingLeft: '48px'
                    }}
                    buttonStyle={{
                      backgroundColor: '#f9fafb',
                      border: errors.phone ? '1px solid #ef4444' : '1px solid #d1d5db',
                      borderRadius: '6px 0 0 6px'
                    }}
                    dropdownStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px'
                    }}
                    preferredCountries={['us', 'gb', 'ca', 'au', 'in']}
                    placeholder="Enter phone number"
                    disabled={isSubmitting}
                  />
                  {errors.phone && (
                    <div className="mt-1 text-sm text-red-600 flex items-center">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      {errors.phone}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Owner Department *</label>
                  <div ref={departmentDropdownRef} className="relative">
                    <button
                      type="button"
                      onClick={() => setDepartmentDropdownOpen(!departmentDropdownOpen)}
                      className={`input-field w-full flex items-center justify-between text-left ${errors.department_id ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`}
                      disabled={isSubmitting}
                    >
                      <span className={formData.department_id ? 'text-gray-900' : 'text-gray-500'}>
                        {formData.department_id
                          ? departmentDropdown.find(d => d.id.toString() === formData.department_id)?.department || 'Select Department'
                          : 'Select Department'}
                      </span>
                      <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${departmentDropdownOpen ? 'transform rotate-180' : ''}`} />
                    </button>
                    {departmentDropdownOpen && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-hidden">
                        <div className="p-2 border-b border-gray-200">
                          <div className="relative">
                            <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                            <input
                              type="text"
                              className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                              placeholder="Search departments..."
                              value={departmentSearchTerm}
                              onChange={(e) => setDepartmentSearchTerm(e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                        </div>
                        <div className="max-h-48 overflow-y-auto">
                          {departmentDropdown
                            .filter(dept => 
                              dept.department.toLowerCase().includes(departmentSearchTerm.toLowerCase())
                            )
                            .map((dept) => (
                              <button
                                key={dept.id}
                                type="button"
                                className="w-full text-left px-4 py-2 text-sm text-gray-900 hover:bg-primary-50 hover:text-primary-900 focus:bg-primary-50 focus:text-primary-900 focus:outline-none"
                                onClick={() => {
                                  handleFieldChange('department_id', dept.id.toString());
                                  setDepartmentDropdownOpen(false);
                                  setDepartmentSearchTerm('');
                                }}
                              >
                                {dept.department}
                              </button>
                            ))}
                          {departmentDropdown.filter(dept => 
                            dept.department.toLowerCase().includes(departmentSearchTerm.toLowerCase())
                          ).length === 0 && (
                            <div className="px-4 py-2 text-sm text-gray-500 text-center">
                              No departments found
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  {errors.department_id && (
                    <div className="mt-1 text-sm text-red-600 flex items-center">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      {errors.department_id}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Address</label>
                <input
                  type="text"
                  className="input-field"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  disabled={isSubmitting}
                  placeholder="Enter address"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Country</label>
                  <input
                    type="text"
                    className="input-field"
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    disabled={isSubmitting}
                    placeholder="Enter country"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">State</label>
                  <input
                    type="text"
                    className="input-field"
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    disabled={isSubmitting}
                    placeholder="Enter state"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">City</label>
                  <input
                    type="text"
                    className="input-field"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    disabled={isSubmitting}
                    placeholder="Enter city"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Post Code</label>
                  <input
                    type="text"
                    className={`input-field ${errors.zip_code ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`}
                    value={formData.zip_code}
                    onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                    disabled={isSubmitting}
                    placeholder="Enter post code"
                  />
                  {errors.zip_code && (
                    <div className="mt-1 text-sm text-red-600 flex items-center">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      {errors.zip_code}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Notes</label>
                <textarea
                  className="input-field"
                  rows={3}
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  disabled={isSubmitting}
                  placeholder="Enter notes"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_active_edit"
                  checked={formData.is_active}
                  onChange={(e) => handleFieldChange('is_active', e.target.checked)}
                  disabled={isSubmitting}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="is_active_edit" className="ml-2 block text-sm font-medium text-gray-700">
                  Active Vendor
                </label>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedVendor(null);
                    resetForm();
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary"
                >
                  {isSubmitting ? <Spinner /> : 'Update Vendor'}
                </button>
              </div>
            </form>
          </div>
        </div>
        </div>
      )}

      {/* Vendor Progress Chart Modal */}
      <VendorProgressChart
        isOpen={showChartModal}
        onClose={() => {
          setShowChartModal(false);
          setSelectedVendor(null);
        }}
        vendorId={selectedVendor?.id || 0}
        vendorName={selectedVendor?.name || ''}
      />

    </div>
  );
};

export default Vendors; 